import { Channel } from "../Com/Com.Interfaces";
import { MemberInterface } from "../Members/Members.Interface";
import { SessionInterface } from "../Sessions/Sessions.Interface";
import { OrgBaseInterface } from "../Shared/OrgBase.Interace";
import { InheritanceStatus, Slug } from "../Shared/Shared.Interfaces";
import { UserInterface } from "../User/User.Interface";

//LINK - libs/entities/src/Escalation/Escalation.Entity.ts
/**
 * Hard-cap staleness for voice CC assignments (ENG-2945): no phone call lasts
 * this long, so any open (`offered`/`accepted`) voice assignment older than
 * this is stale by definition. Used by the routing-time pool guard
 * (RoutingEngine) and the bot's VoiceAssignmentSweep — lives here (not
 * server-helpers) so RoutingEngine's unit test doesn't have to load the
 * server-helpers barrel, which drags in FMS decorators ts-jest can't handle.
 */
export const VOICE_ASSIGNMENT_MAX_AGE_MS = 6 * 60 * 60 * 1000;

export enum EscalationPlatform {
  SWIVL = "swivl", // Pending Opt In
  TEAMS = "teams",
  SLACK = "slack"
}

export enum EscalationStatus {
  PENDING = "pending", // Pending Opt In
  ASSIGNED = "assigned",
  ACTIVE = "active",
  HANDLED = "handled",

  // MISSED   = "missed", // This means agents were tried but none found
  // IGNORED  = "ignored",
  // UNAVAILABLE = "unavailable",
  // OUTSIDE_HOURS = "outside of hours",
  // OPT_OUT = "opt out",
  TIMED_OUT = "timed out", // This means the no agent accepted
  OUTSIDE_HOURS = "outside of hours",
  NO_AGENT_AVAILABLE = "no agent available",
  USER_IS_BLOCKED = "user is blocked"
}

/**
 * @tsoaModel
 */

export interface EscalationRedisData {
  id: number; // ID of the escalation
  groupSlug: string;
  userId: number;
  status: EscalationStatus;
  member?: { id: number; name: string };
  createdAt: string;
}

export function EscalationToRedisData(escalation: EscalationInterface): EscalationRedisData {
  return {
    id: escalation.id,
    groupSlug: escalation.groupSlug,
    userId: escalation.userId,
    status: escalation.status,
    member: escalation.assignedMember
      ? {
          id: escalation.assignedMember.id,
          name: [escalation.assignedMember?.account.firstName, escalation.assignedMember?.account.lastName].filter(Boolean).join(" ")
        }
      : undefined,
    createdAt: typeof escalation.createdAt === "string" ? escalation.createdAt : escalation.createdAt?.toISOString() || ""
  };
}

export function EscalationIsOpen(escalation: EscalationInterface | EscalationRedisData): boolean {
  return (
    escalation.status === EscalationStatus.PENDING || escalation.status === EscalationStatus.ASSIGNED || escalation.status === EscalationStatus.ACTIVE
  );
}
export function EscalationIsAssignedToMember(escalation?: EscalationInterface, memberId?: number): boolean {
  return (
    !!escalation &&
    !!memberId &&
    escalation.resolved === false &&
    escalation.status === EscalationStatus.ASSIGNED &&
    escalation.assignedMemberId === memberId
  );
}

export interface EscalationInterface extends OrgBaseInterface {
  sessionId: number;
  session?: SessionInterface;

  // groupId: string
  groupSlug: string;
  groupName: string;
  platform: EscalationPlatform;
  status: EscalationStatus;
  assignedTimestamp?: number;
  nextAgentTimestamp?: number;
  assignedMemberId?: number;
  assignedMember?: MemberInterface;
  searchingForNextAgentAt?: Date; // When we started looking for the next agent.

  history?: EscalationHistory[];
  user?: UserInterface;
  userId: number;

  locationId?: number;
  reason?: string;

  membersAttemptedIds?: number[];
  acknowledgedMemberIds?: number[]; // Members that have acknowledged this escalation

  resolved: boolean;
  // Add what quest / qNode it was recorded in.
  takeoverStarted?: number;
  takeoverEnded?: number;
  slackMessages?: EscalationSlackMessages;

  agentInitiated?: boolean;

  initialMemberStatus?: MemberStatus;

  summary?: string;

  channel: Channel;

  version: number;
}

export interface AgentsStatus {
  groupId: number;
  groupSlug: string;
  groupName: string;
  numberOfAgents: number;
  noAgentsAvailable: boolean;
  ignoreAvailability: boolean;
  available: { id: number; name: string }[];
  unvailable: { id: number; name: string }[];
  notAllowed: { id: number; name: string }[];
  agentAvailable: { [key: string]: boolean };
  error?: any;
}

export interface MemberStatus {
  groupId: number;
  groupSlug: string;
  groupName: string;
  numberOfMembers: number;
  noMembersAvailable: boolean;
  ignoreAvailability: boolean;
  available: { id: number; name: string }[];
  unavailable: { id: number; name: string }[];
  notAllowed: { id: number; name: string }[];
  memberAvailable: { [key: string]: boolean };
  error?: any;
}

/**
 * @tsoaModel
 */

export enum EscalationErrorType {
  TIMED_OUT = "timed out",
  REASSIGNED = "reassigned",
  UNKNOWN = "unknown"
}

export interface EscalationUpdatedReturnData {
  success: boolean;
  error?: EscalationErrorType;
  escalation: EscalationInterface;
  conferenceName?: string;
}

export enum EscalationHistoryEvent {
  CREATED = "CREATED",
  AGENT_INITIATED = "agent initiated",
  AVAILABLE_AGENTS_LOOKED_UP = "available agents looked up", // DEPRECATED
  ASSIGNED_TO_AGENT = "assigned to agent",
  AGENT_ACCEPTED = "agent accepted",
  AGENT_REJECT = "agent reject",
  AGENT_MISSED = "agent missed",
  CONVERSATION_ENDED = "conversation ended",
  ACKNOWLEDGED = "acknowledged",
  AWAITING_AGENT = "awaiting agent",

  TIMED_OUT = "timed out",
  NO_AGENT_AVAILABLE = "no agent available",
  OUTSIDE_HOURS = "outside hours",
  USER_IS_BLOCKED = "user is blocked",
  CLOSED_BY_SYSTEM = "closed by system" // This is when the escalation is closed by the system, e.g., during cleanup.
}

export interface EscalationHistory {
  event: EscalationHistoryEvent;
  member?: MemberInterface;
  memberId?: number;
  date: Date;
  message?: string;
  data?: any;
}

export interface EscalationsGroupInterface extends OrgBaseInterface {
  slug: string;
  inheritance?: InheritanceStatus;

  initialMessage: string; // Sent right away.

  wontWaitQuestSlug?: string;
  outsideHoursQuestSlug?: string;
  noAvailableAgentsQuestSlug?: string;

  multipleEscalationsResponse: string;

  agentMessage?: string;

  platform: EscalationPlatform;
  name: string;
  hours: HoursOfOperationInterface;
  queue: boolean;
  numberOfAgentsToTry: number;
  secondsToWaitForAgent: number;

  roles: Slug[];

  slackChannelId?: string;
  groupChannelMessage?: string; // Optional message sent in to group channel informing agents of issues.

  ignoreAvailability?: boolean;

  ignoreHours?: boolean;

  queuingStrategy: EscalationQueuingStrategyType;

  membersQueuingOrder: number[];
}

export interface EscalationPlatformSettings {
  swivl?: any;
  slack?: any;
  teams?: any;
}

export interface HoursOfOperationInterface {
  timezone: string;
  sun: HoursInterface;
  mon: HoursInterface;
  tue: HoursInterface;
  wed: HoursInterface;
  thu: HoursInterface;
  fri: HoursInterface;
  sat: HoursInterface;
}

export interface HoursInterface {
  // 24 hour based.
  open: boolean;
  start: string;
  end: string; // HH:mm format
}

export type EscalationSlackMessages = {
  [key in string]?: EscalationSlackMessage; // Note that "key in".
};
export interface EscalationSlackMessage {
  type: EscalationSlackMessageType;
  ts: string;
  channelId: string;
  agentId?: string;
}

export enum EscalationSlackMessageType {
  CHANNEL = "channel",
  AGENT = "agent"
}

export enum EscalationQueuingStrategyType {
  LONGEST_IDLE_FIRST = "longest_idle_first", //Longest Idle First
  RING_SPECIFIC_MEMBER = "ring_specific_member" // Ring a Specific Member
}

export function TwilioConferenceName(orgId: number, escalationId: number): string {
  return `org-${orgId}-escalation-${escalationId}`;
}
